/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { createElement } from 'react';
/**
 * True when the query matched more rows than this block holds. The producer caps
 * the page and reports the full count in `total_rows`; legacy producers omit it
 * (or send a non-number), which must read as "not partial" rather than guessing.
 */
export function isPartialResult(block) {
    const { total_rows: total, displayed_rows: displayed } = block;
    return typeof total === 'number' && typeof displayed === 'number' && total > displayed;
}
export function mapColumns(columns) {
    return columns.map((col) => {
        const base = {
            dataKey: col.key,
            label: col.label,
            align: col.align || 'left'
        };
        if (col.link_template) {
            const template = col.link_template;
            base.renderer = ({ item, dataKey }) => {
                const cellValue = item[dataKey];
                if (cellValue == null || cellValue === '')
                    return createElement('span', null, String(cellValue !== null && cellValue !== void 0 ? cellValue : ''));
                const href = template.replace('{value}', encodeURIComponent(String(cellValue)));
                let isSafe = false;
                try {
                    const parsed = new URL(href, window.location.origin);
                    isSafe = ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
                }
                catch (_a) {
                    isSafe = false;
                }
                if (!isSafe)
                    return createElement('span', null, String(cellValue));
                return createElement('a', {
                    href,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    onClick: (e) => e.stopPropagation()
                }, String(cellValue));
            };
        }
        return base;
    });
}
/**
 * Coerce any cell value to a display string for CSV export. An object cell
 * (e.g. a joined record from the AI data_table) would otherwise stringify to
 * "[object Object]"; prefer a human label, falling back to JSON. Mirrors the
 * render-side coercion in Table.getCellTemplate and the backend _scalarize_cell.
 */
export function toDisplayString(value) {
    var _a, _b, _c;
    if (value == null)
        return '';
    if (typeof value === 'object') {
        const obj = value;
        const label = (_c = (_b = (_a = obj.name) !== null && _a !== void 0 ? _a : obj.display_name) !== null && _b !== void 0 ? _b : obj.label) !== null && _c !== void 0 ? _c : obj.title;
        if (typeof label === 'string' || typeof label === 'number')
            return String(label);
        // JSON.stringify throws on circular references — guard so a malformed cell
        // can never crash the CSV export.
        try {
            return JSON.stringify(value);
        }
        catch (_d) {
            return '[complex object]';
        }
    }
    return String(value);
}
export function escapeCsvValue(value) {
    let str = value == null ? '' : String(value);
    if (str.length > 0 && '=+-@'.includes(str[0])) {
        str = `'${str}`;
    }
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}
export function exportToCsv(block) {
    const { columns, rows, title } = block;
    const header = columns.map((c) => escapeCsvValue(c.label)).join(',');
    const body = rows
        .map((row) => columns.map((c) => escapeCsvValue(toDisplayString(row[c.key]))).join(','))
        .join('\n');
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'export'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}
